import { Injectable } from '@angular/core';
import { TokenService, AlertService, CurrencyService } from '../index';
import { environment as env } from 'src/environments/environment';
import { MenuItem, CurrencyList } from 'src/app/models/shared/layout/layout.interface';
import { BehaviorSubject, catchError, finalize, map, Observable, of, Subject, tap, throwError } from 'rxjs';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { NgxSpinnerService } from 'ngx-spinner';
import { Router } from '@angular/router';
import { WebTypeConstant } from 'src/app/shared/constants/common-constant';
import { LoginPayload } from 'src/app/models/shared/auth/auth.interface';
@Injectable({
    providedIn: 'root'
})
export class AuthService {
    private themeUrl: string | null = null;

    private webTypeSubject = new BehaviorSubject<string | null>(null);
    public webType$ = this.webTypeSubject.asObservable();

    private themeSubject = new BehaviorSubject<string | null>(null);
    public theme$ = this.themeSubject.asObservable();

    private faviconSubject = new BehaviorSubject<string | null>(null);
    public favicon$ = this.faviconSubject.asObservable();

    private isAuthenticatedSubject = new Subject<boolean>();

    constructor(
        private token: TokenService,
        private spinner: NgxSpinnerService,
        private httpClient: HttpClient,
        private alertService: AlertService,
        private router: Router,
        private currencyService: CurrencyService
    ) {
        this.isAuthenticatedSubject.next(false);
    }

    /*  --- START - Get Web Type by using domain --- */
    public isAuthenticated(): boolean {
        const token: string | null = this.token.getLocalToken(
            env.TokenKeys.AccessToken
        );

        return token ? true : false;
    }

    getWebType(): Observable<string | null> {
        return this.webTypeSubject.asObservable();
    }

    // When setting WebType, also update the subject
    setWebType(webType: string | null) {
        this.token.setLocalToken(env.TokenKeys.WebType, webType);
        this.webTypeSubject.next(webType);
    }

    getFavicon(): string | null {
        return this.faviconSubject.value;
    }

    getTheme(): string | null {
        return this.themeUrl;
    }
    /*  --- END - Get Web Type by using domain --- */
    login(loginPayload: LoginPayload): Observable<any> {
        const endPoint: string = this.getAuthEndPoint('getauthenticate', loginPayload.webType);

        const merchantCode = this.token.getLocalToken(env.TokenKeys.MerchantCode);
        const merchantId = Number(this.token.getLocalToken(env.TokenKeys.MerchantId));
        const currency = this.currencyService.getCurrency();

        const webType = loginPayload.webType;

        let payload = {
            authenticateType: loginPayload.authenticateType,
            username: loginPayload.username,
            email: loginPayload.email,
            phoneNo: loginPayload.phoneNo,
            password: loginPayload.password,
            webType: webType,
            ...(webType === WebTypeConstant.MERCHANT ? { code: merchantCode, id: merchantId } : {}),
            ...(webType === WebTypeConstant.MERCHANTSTAFF || webType === WebTypeConstant.AGENTUNLIMITED || webType === WebTypeConstant.AGENTSTAFFUNLIMITED ? { merchantCode: merchantCode, merchantId: merchantId } : {}),
            ...(webType === WebTypeConstant.AGENTUNLIMITED || webType === WebTypeConstant.AGENTSTAFFUNLIMITED ? { currencyCode: currency.currencyCode, currencyId: currency.currencyId } : {}),
        };

        this.spinner.show();
        return this.httpClient.post<any>(endPoint, payload).pipe(
            tap(resp => {
                if (resp.code === 1 && resp.data.token) {
                    this.clearAccessToken();

                    this.token.setLocalToken(env.TokenKeys.AccessToken, resp.data.token);
                    this.token.setLocalToken(env.TokenKeys.RefreshToken, resp.data.refreshToken);

                    const userInfo = {
                        id: resp.data.profile.id,
                        name: resp.data.profile.name,
                        username: resp.data.profile.username,
                        email: resp.data.profile.email,
                        phoneNo: resp.data.profile.phoneNo,
                        countryCode: resp.data.profile.countryCode,
                        countryId: resp.data.profile.countryId,
                        countryPhoneCode: resp.data.profile.countryPhoneCode,
                    }

                    let menuList: MenuItem[] = [];

                    if (webType == WebTypeConstant.ADMIN) {
                        menuList = this.getRestructuredMenuList(resp.data.profile.authenticateAdminMenuList);
                    } else if (webType == WebTypeConstant.MERCHANT) {
                        menuList = this.getRestructuredMenuList(resp.data.profile.authenticateMerchantMenuList);
                    } else if (webType == WebTypeConstant.ADMINSTAFF) {
                        menuList = this.getRestructuredMenuList(resp.data.profile.authenticateAdminStaffMenuList);
                    } else if (webType == WebTypeConstant.MERCHANTSTAFF) {
                        menuList = this.getRestructuredMenuList(resp.data.profile.authenticateMerchantStaffMenuList);
                    } else if (webType == WebTypeConstant.AGENTUNLIMITED) {
                        menuList = this.getRestructuredMenuList(resp.data.profile.authenticateUnAgentMenuList);
                    } else if (webType == WebTypeConstant.AGENTSTAFFUNLIMITED) {
                        menuList = this.getRestructuredMenuList(resp.data.profile.authenticateUnAgentStaffMenuList);
                    }

                    this.token.setLocalToken(env.TokenKeys.UserInfo, JSON.stringify(userInfo));
                    this.token.setLocalToken(env.TokenKeys.MenuList, JSON.stringify(menuList));

                    // Only init when webtype = merchant | merchantstaff
                    if (webType === WebTypeConstant.MERCHANT || webType === WebTypeConstant.MERCHANTSTAFF) {
                        this.token.removeLocalToken(env.TokenKeys.CurrencyList);
                        this.token.removeLocalToken(env.TokenKeys.Currency);

                        let currencyList: CurrencyList[] = [];

                        if (webType == WebTypeConstant.MERCHANT) {
                            currencyList = resp.data.profile.authenticateMerchantSettingCurrencyList;
                        } else if (webType == WebTypeConstant.MERCHANTSTAFF) {
                            currencyList = resp.data.profile.authenticateMerchantStaffSettingCurrencyList;
                        }

                        const encryptedCurrencyList = JSON.stringify(currencyList);
                        this.token.setLocalToken(env.TokenKeys.CurrencyList, encryptedCurrencyList);

                        // Selected Currency Token
                        const Currency = this.token.getLocalToken(env.TokenKeys.Currency);

                        if (!Currency) {
                            const myrCurrency = currencyList.find(currency => currency.currencyCode === env.DefaultCurrencyCode);

                            const selectedCurrency = myrCurrency ?? [];
                            const encryptedCurrency = JSON.stringify(selectedCurrency);
                            this.token.setLocalToken(env.TokenKeys.Currency, encryptedCurrency);
                        }
                    }

                    this.isAuthenticatedSubject.next(true);
                    this.alertService.success('login');
                } else {
                    // this.alertService.showErrorByResponseCode(resp.code);
                }
            }),
            map(resp => resp),
            catchError((error: HttpErrorResponse) => {
                this.alertService.error('system');
                return throwError(() => new Error(error.message || 'An error occurred')); // ✅ Throw error
            }),
            finalize(() => this.spinner.hide())
        );
    }

    refreshToken(): Observable<any> {
        const refreshToken = this.token.getLocalToken(env.TokenKeys.RefreshToken) ?? "";
        const webType = this.token.getLocalToken(env.TokenKeys.WebType);
        const payload = {
            refreshToken: refreshToken
        }
        let controller = ''
        let action = ''

        switch (webType) {
            case WebTypeConstant.ADMIN:
            case WebTypeConstant.ADMINSTAFF:
                controller = WebTypeConstant.ADMIN.toLowerCase();
                action = WebTypeConstant.ADMIN.toLowerCase();
                break;
            case WebTypeConstant.MERCHANT:
            case WebTypeConstant.MERCHANTSTAFF:
                controller = WebTypeConstant.MERCHANT.toLowerCase();
                action = WebTypeConstant.MERCHANT.toLowerCase();
                break;
            case WebTypeConstant.AGENTUNLIMITED: // Agent have diff controller name 
                controller = 'UnAgent'
                action = 'UnAgent'
                break;
            case WebTypeConstant.AGENTSTAFFUNLIMITED: // Agent have diff controller name 
                controller = 'UnAgentStaff'
                action = 'UnAgentStaff'
                break;
            default:
                break;
        }

        return this.httpClient.post<any>(`${env.ApiConfig.AuthUrl}/api/authenticate${controller}/refreshauthenticate${action}`, payload).pipe(
            tap(resp => {
                if (resp) {
                    if (resp.code == 1) {
                        // this.clearAccessToken();
                        // this.token.setLocalToken(env.TokenKeys.AccessToken, resp.Token);
                        // this.token.setLocalToken(env.TokenKeys.RefreshToken, resp.RefreshToken);
                    } else {
                        this.alertService.showErrorByResponseCode(resp.code);
                        // this.clearAccessToken();
                    }
                }
            }),
            catchError(error => {
                this.alertService.error('unauthorized');
                this.clearAccessToken();
                return throwError(() => error);
            })
        );
    }

    getAccessToken(): string | null {
        return this.token.getLocalToken(env.TokenKeys.AccessToken);
    }

    public logout(): Observable<boolean> {
        const payload = {
            token: this.token.getLocalToken(env.TokenKeys.AccessToken)
        };
        const webType = this.token.getLocalToken(env.TokenKeys.WebType);
        const endPoint: string = this.getAuthEndPoint('removeauthenticate', webType);

        return this.httpClient.post<any>(endPoint, payload).pipe(
            map(resp => {
                if (resp.code && resp.code == 1) {
                    this.alertService.success('logout');
                }

                // this.token.removeLocalToken(env.TokenKeys.CurrencyList);
                // this.token.removeLocalToken(env.TokenKeys.Currency);

                let route = '/auth/login';

                if (webType == WebTypeConstant.AGENTUNLIMITED || webType == WebTypeConstant.AGENTSTAFFUNLIMITED) {
                    const currency = this.currencyService.getCurrency();
                    const currencyCode = currency?.currencyCode ?? '';

                    if (currencyCode) {
                        route = `/${currencyCode.toLowerCase()}/auth/login`;
                    } else {
                        route = '/currency';
                    }
                }

                this.clearAccessToken();
                this.clearRedirectUrl();
                this.isAuthenticatedSubject.next(false);

                this.router.navigateByUrl(route);
                return resp?.code === 1;

            }),
            catchError(error => {
                return of(false);
            })
        );
    }

    public clearAccessToken() {
        this.token.removeLocalToken(env.TokenKeys.AccessToken);
        this.token.removeLocalToken(env.TokenKeys.RefreshToken);
    }

    public clearUserToken() {
        this.token.removeLocalToken(env.TokenKeys.MenuList);
        this.token.removeLocalToken(env.TokenKeys.UserInfo);
    }

    public forceLogout(): void {
        const lastUrl = this.router.url;
        this.token.setSessionToken(env.TokenKeys.RedirectUrl, lastUrl);

        this.clearAccessToken();
        // this.token.removeLocalToken(env.TokenKeys.CurrencyList);
        // this.token.removeLocalToken(env.TokenKeys.Currency);

        this.alertService.error('unauthorized');
        this.isAuthenticatedSubject.next(false);

        const webType = this.token.getLocalToken(env.TokenKeys.WebType);
        let route = '/auth/login';

        if (webType == WebTypeConstant.AGENTUNLIMITED || webType == WebTypeConstant.AGENTSTAFFUNLIMITED) {
            const currency = this.currencyService.getCurrency();
            const currencyCode = currency?.currencyCode ?? '';

            if (currencyCode) {
                route = `/${currencyCode.toLowerCase()}/auth/login`;
            } else {
                route = '/currency';
            }
        }

        this.router.navigateByUrl(route);
    }


    /* Restructure Menu List for local storage */
    getRestructuredMenuList(menuList: any): MenuItem[] {
        let menuList2: MenuItem[] = [];

        if (menuList) {
            try {
                menuList2 = menuList as MenuItem[];
            } catch (error) {
                console.error('Invalid menu list JSON:', error);
            }
        }

        if (menuList2.length > 0) {
            return this.buildSidebarMenu(menuList2);
        }

        return [];
    }

    buildSidebarMenu(menuItems: MenuItem[]): MenuItem[] {
        const menuMap = new Map<number, MenuItem>();
        const rootItems: MenuItem[] = [];
        // const getWebType = this.getWebType()?.toLowerCase();
        const getWebType = this.token.getLocalToken(env.TokenKeys.WebType);
        let webType = '';

        switch (getWebType) {
            case WebTypeConstant.ADMIN:
            case WebTypeConstant.ADMINSTAFF:
                webType = WebTypeConstant.ADMIN.toLowerCase();
                break;
            case WebTypeConstant.MERCHANT:
            case WebTypeConstant.MERCHANTSTAFF:
                webType = WebTypeConstant.MERCHANT.toLowerCase();
                break;
            case WebTypeConstant.AGENTUNLIMITED:
            case WebTypeConstant.AGENTSTAFFUNLIMITED:
                webType = WebTypeConstant.AGENTUNLIMITED.toLowerCase();
                break;
            default:
                this.router.navigate(['/auth/login']);
        }


        menuItems.forEach(item => {
            menuMap.set(item.parentId,
                {
                    ...item,
                    children: [],
                    isOpen: false,
                    isActive: false,
                    // route: `/${webType}` + item.route
                    route: item.route
                }
            );
        });

        menuItems.forEach(item => {
            if (item.dependentId === null) {
                rootItems.push(menuMap.get(item.parentId)!);
            } else {
                const parent = menuMap.get(item.dependentId);
                if (parent) {
                    parent.children?.push(menuMap.get(item.parentId)!);
                }
            }
        });

        const sortMenu = (items: MenuItem[]) => {
            items.sort((a, b) => a.sequence - b.sequence);
            items.forEach(item => {
                if (item.children && item.children.length > 0) {
                    sortMenu(item.children);
                }
            });
        };

        sortMenu(rootItems);
        return rootItems;
    }

    setRedirectUrl(url: string) {
        this.token.setSessionToken(env.TokenKeys.RedirectUrl, url);
    }

    getRedirectUrl(): string | null {
        return this.token.getSessionToken(env.TokenKeys.RedirectUrl);
    }

    clearRedirectUrl() {
        this.token.removeSessionToken(env.TokenKeys.RedirectUrl);
    }

    private getAuthEndPoint(authType: string, webType: any) {
        let translatedWebType: string = '';

        switch (webType) {
            case 'Admin':
                translatedWebType = 'Admin';
                break;
            case 'AdminStaff':
                translatedWebType = 'AdminStaff';
                break;
            case 'Merchant':
                translatedWebType = 'Merchant';
                break;
            case 'MerchantStaff':
                translatedWebType = 'MerchantStaff';
                break;
            case 'AgentUnlimited':
                translatedWebType = 'UnAgent';
                break;
            case 'AgentStaffUnlimited':
                translatedWebType = 'UnAgentStaff';
                break;
            default:
                throw new Error('Invalid WebType specified')
        }

        return `${env.ApiConfig.AuthUrl}/api/authenticate${translatedWebType}/${authType}${translatedWebType.toLocaleLowerCase()}`;
    }
}
